import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface JobDigestLog {
  id: string;
  triggered_by: string;
  status: string;
  jobs_count: number;
  recipients_count: number;
  sent_count: number;
  failed_count: number;
  skipped_count: number | null;
  pending_count: number | null;
  batch_limit: number | null;
  is_complete: boolean | null;
  duration_ms: number | null;
  error_message: string | null;
  errors: unknown;
  created_at: string;
}

export function useJobDigestLogs(limit = 50) {
  return useQuery({
    queryKey: ["job-digest-logs", limit],
    queryFn: async (): Promise<JobDigestLog[]> => {
      const { data, error } = await supabase
        .from("job_digest_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as unknown as JobDigestLog[];
    },
    staleTime: 30_000,
  });
}

export interface DigestPreviewJob {
  id: string;
  title: string;
  company_name: string;
  work_model: string | null;
  created_at: string;
}

export function useJobDigestPreview() {
  return useQuery({
    queryKey: ["job-digest-preview"],
    queryFn: async (): Promise<DigestPreviewJob[]> => {
      const { data, error } = await supabase
        .from("curated_jobs")
        .select("id, title, company_name, work_model, created_at")
        .eq("status", "active")
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return (data ?? []) as DigestPreviewJob[];
    },
    staleTime: 60_000,
  });
}

export interface DigestDeliveryStats {
  sentThisMonth: number;
  deliveriesTotal: number;
}

/** Emails enviados no mês corrente (para acompanhar a cota do Resend). */
export function useDigestDeliveryStats() {
  return useQuery({
    queryKey: ["job-digest-delivery-stats"],
    queryFn: async (): Promise<DigestDeliveryStats> => {
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);

      const { data: logs, error: logsError } = await supabase
        .from("job_digest_logs")
        .select("sent_count, created_at")
        .gte("created_at", monthStart.toISOString());
      if (logsError) throw logsError;

      const { count, error: countError } = await supabase
        .from("job_digest_deliveries")
        .select("id", { count: "exact", head: true });
      if (countError) throw countError;

      return {
        sentThisMonth: (logs ?? []).reduce((acc, l) => acc + (l.sent_count ?? 0), 0),
        deliveriesTotal: count ?? 0,
      };
    },
    staleTime: 30_000,
  });
}
